import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Download as DownloadIcon,
  PictureAsPdf as PdfIcon,
  Description as WordIcon,
} from "@mui/icons-material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  GridToolbarColumnsButton,
} from "@mui/x-data-grid";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { 
  getMilestoneList, 
  getMilestonePdfReport, 
  clearMilestonePdfReport 
} from "../../../store/wafrQuestionnaire/wafrQuestionnaireSlice";

const MilestonesPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const {
    workloadId,
    orgCode,
    accCode,
    accNumber,
    assessmentName,
    frameworkName,
    ownerName,
    approverName,
  } = location.state || {};

  const { isGettingMilestoneList, milestoneList, getMilestoneListError } =
    useSelector((state) => state.wafrQuestionnaire);

  // Add selector for Milestone PDF report
  const { 
    isGettingMilestonePdfReport, 
    getMilestonePdfReportError, 
    milestonePdfReport 
  } = useSelector((state) => state.wafrQuestionnaire);

  // Add state for Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const [currentDownloadMilestone, setCurrentDownloadMilestone] = useState(null);
  const [formatDialog, setFormatDialog] = useState({
    open: false,
    selectedMilestone: null,
  });

  console.log("🚀 ~ MilestonesPage ~ milestoneList:", milestoneList);

  useEffect(() => {
    if (workloadId && accCode) {
      dispatch(getMilestoneList({ workloadId, accCode }));
    }
  }, [dispatch, workloadId, accCode]);

  // Open format selection dialog
  const handleDownloadClick = (rowData) => {
    setFormatDialog({
      open: true,
      selectedMilestone: rowData,
    });
  };

  // Milestone Report handler
  const handleMilestoneReport = (format) => {
    const rowData = formatDialog.selectedMilestone;
    const payload = {
      orgCode: orgCode,
      accountCode: accCode,
      accountNumber: accNumber,
      workloadId: workloadId,
      assessmentName: assessmentName,
      frameworkName: frameworkName,
      workloadArn: location.state?.workloadArn,
      ownerName: ownerName,
      approverName: approverName,
      milestoneName: rowData.MilestoneName,
      format: format,
    };
    
    // Store the milestone name for the download
    setCurrentDownloadMilestone(rowData.MilestoneName);
    
    console.log(`Calling Milestone ${format.toUpperCase()} report API with payload:`, payload);
    dispatch(getMilestonePdfReport(payload));
    
    // Close the dialog
    setFormatDialog({ open: false, selectedMilestone: null });
  };

  // Handle Milestone PDF report download when data is available
  useEffect(() => {
    if (milestonePdfReport && !isGettingMilestonePdfReport) {
      try {
        // Open the pre-signed URL in a new tab for download
        const newWindow = window.open(milestonePdfReport, '_blank');
        
        if (newWindow) {
          // Show success message
          setSnackbarMessage(`Milestone report download opened in new tab!`);
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        } else {
          // If popup blocked, show error
          setSnackbarMessage("Popup blocked. Please allow popups and try again.");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
        
        // Clear the PDF report state after successful download
        dispatch(clearMilestonePdfReport());
        setCurrentDownloadMilestone(null);
      } catch (error) {
        console.error(`Error opening Milestone report:`, error);
        setSnackbarMessage(`Failed to open Milestone report. Please try again.`);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        dispatch(clearMilestonePdfReport());
        setCurrentDownloadMilestone(null);
      }
    }
  }, [milestonePdfReport, isGettingMilestonePdfReport, dispatch, currentDownloadMilestone]);

  // Handle Milestone report errors
  useEffect(() => {
    if (getMilestonePdfReportError) {
      console.error(`Milestone Report Error:`, getMilestonePdfReportError);
      setSnackbarMessage(getMilestonePdfReportError);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      // Clear the error state after a delay
      setTimeout(() => {
        dispatch(clearMilestonePdfReport());
        setCurrentDownloadMilestone(null);
      }, 3000);
    }
  }, [getMilestonePdfReportError, dispatch]);

  // Handle Snackbar close
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const rows = React.useMemo(() => {
    if (!milestoneList) return [];
    return milestoneList.map((milestone) => ({
      id: `${milestone.workloadId}-${milestone.MilestoneNumber}`,
      MilestoneNumber: milestone.MilestoneNumber,
      MilestoneName: milestone.MilestoneName,
      accountNumber: milestone.accountNumber,
      accountCode: milestone.accountCode,
      orgCode: milestone.orgCode,
      createdBy: milestone.createdBy,
      createdDate: milestone.createdDate,
      lastModifiedBy: milestone.lastModifiedBy,
      lastModifiedDate: milestone.lastModifiedDate,
    }));
  }, [milestoneList]);

  const columns = [
    {
      field: "MilestoneNumber",
      headerName: "Milestone",
      width: 120,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "MilestoneName",
      headerName: "Milestone Name",
      width: 200,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "accountNumber",
      headerName: "Account Number",
      width: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "accountCode",
      headerName: "Account Code",
      width: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "orgCode",
      headerName: "Org Code",
      width: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "createdBy",
      headerName: "Created By",
      width: 200,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "createdDate",
      headerName: "Created Date",
      width: 180,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => new Date(params.row.createdDate).toDateString(),
    },
    {
      field: "lastModifiedBy",
      headerName: "Last Modified By",
      width: 200,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "lastModifiedDate",
      headerName: "Last Modified Date",
      width: 180,
      align: "center",
      headerAlign: "center",
      renderCell: (params) =>
        new Date(params.row.lastModifiedDate).toDateString(),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const row = params.row;
        const rowId = params.row.id; // Unique ID for the row

        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap="8px"
          >
            {/* View Button */}
            <Button
              variant="outlined"
              size="small"
              onClick={() =>
                history.push(`/workload-assessment/milestonesSummary`, {
                  workloadId: workloadId,
                  orgCode: orgCode,
                  accCode: accCode,
                  accNumber: accNumber,
                  assessmentName: assessmentName,
                  frameworkName: frameworkName,
                  ownerName: ownerName,
                  approverName: approverName,
                  milestoneName: params.row.MilestoneName,
                  milestoneNumber: params.row.MilestoneNumber,
                })
              }
              sx={{
                textTransform: "none",
                fontWeight: 500,
                borderRadius: "20px",
                padding: "2px 12px",
                borderColor: "#1976d2",
                color: "#1976d2",
                "&:hover": {
                  backgroundColor: "#E3F2FD",
                  borderColor: "#1565c0",
                },
              }}
            >
              View
            </Button>

            {/* Download Icon */}
            <IconButton
              size="small"
              onClick={() => handleDownloadClick(row)}
              disabled={isGettingMilestonePdfReport && currentDownloadMilestone === row.MilestoneName}
              sx={{
                color: "#666",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
                "&:disabled": {
                  opacity: 0.6,
                },
              }}
            >
              {isGettingMilestonePdfReport && currentDownloadMilestone === row.MilestoneName ? (
                <CircularProgress size={16} />
              ) : (
                <DownloadIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
        );
      },
    },
  ];

  console.log("🚀 ~ MilestonesPage ~ processed rows:", rows);

  // Custom Toolbar Component
  function CustomToolbar() {
    return (
      <GridToolbarContainer
        sx={{
          p: 2,
          borderBottom: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <GridToolbarColumnsButton
          sx={{
            textTransform: "none",
            color: "#1976d2",
            border: "1px solid #1976d2",
            borderRadius: "4px",
            height: 36,
            "&:hover": {
              backgroundColor: "#e3f2fd",
              borderColor: "#1565c0",
            },
            "& .MuiSvgIcon-root": {
              marginRight: 1,
            },
          }}
        />
      </GridToolbarContainer>
    );
  }



  return (
    <div>
      <Box
        sx={{
          padding: "1.5rem 2rem",
          mt: "5rem",
          backgroundColor: "#F9F9F9",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            padding: "0.5rem 1rem",
            borderRadius: "8px",
            marginBottom: "0.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#f9fafe",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "1.5rem" }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: "600", color: "#2a3c50" }}
            >
              Milestones
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b7280" }}>
              | Assessment: {assessmentName || "N/A"}
            </Typography>
          </Box>

          <Button
            onClick={() => history.goBack()}
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              borderColor: "#1976d2",
              color: "#1976d2",
              "&:hover": {
                borderColor: "#1565c0",
                backgroundColor: "#e3f2fd",
              },
            }}
          >
            Back
          </Button>
        </Box>

        {/* Content */}
        {isGettingMilestoneList ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ height: "50vh" }}
          >
            <CircularProgress />
          </Box>
        ) : getMilestoneListError ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{
              height: "50vh",
              backgroundColor: "#fef8f8",
              border: "1px solid #f44336",
              borderRadius: "8px",
              padding: "2rem",
              textAlign: "center",
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 48, color: "#f44336", mb: 2 }} />
            <Typography
              variant="h6"
              fontWeight="bold"
              color="#f44336"
              sx={{ mb: 1 }}
            >
              Something Went Wrong
            </Typography>
            <Typography variant="body1" color="#333" sx={{ mb: 2 }}>
              {getMilestoneListError}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() =>
                dispatch(getMilestoneList({ workloadId, accCode }))
              }
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                borderColor: "#1976d2",
                color: "#1976d2",
                "&:hover": {
                  borderColor: "#1565c0",
                  backgroundColor: "#e3f2fd",
                },
              }}
            >
              Retry
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              flexGrow: 1,
              overflow: "hidden",
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={100}
              rowsPerPageOptions={[100]}
              disableSelectionOnClick
              hideFooterSelectedRowCount
              components={{
                Toolbar: CustomToolbar,
                NoRowsOverlay: () => (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <Typography>No rows</Typography>
                  </Box>
                ),
              }}
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    accountNumber: false,
                    accountCode: false,
                    orgCode: false,
                  },
                },
              }}
              sx={{
                backgroundColor: "white",
                borderRadius: "8px",
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-cell": {
                  py: 0.5,
                  fontSize: "14px",
                  fontFamily: "Arial, sans-serif",
                  textAlign: "center",
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#F5F5F5",
                  fontWeight: "bold",
                  fontSize: "14px",
                  color: "#333",
                  position: "relative",
                },
                "& .MuiDataGrid-footerContainer": {
                  borderTop: "1px solid #e0e0e0",
                  backgroundColor: "#F5F5F5",
                },
              }}
            />
          </Box>
        )}

        {/* Snackbar for Milestone PDF download status */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
        
        {/* Debug info for snackbar */}
        {console.log('🔍 Debug - Snackbar props:', {
          open: snackbarOpen,
          message: snackbarMessage,
          severity: snackbarSeverity
        })}

        {/* Format Selection Dialog */}
        <Dialog
          open={formatDialog.open}
          onClose={() => setFormatDialog({ open: false, selectedMilestone: null })}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)',
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Select Report Format
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
              Choose your preferred format for downloading the milestone report
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <List sx={{ pt: 0 }}>
              <ListItemButton
                onClick={() => handleMilestoneReport('pdf')}
                sx={{
                  borderRadius: 1,
                  mb: 1,
                  border: '1px solid #e0e0e0',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    borderColor: '#1976d2',
                  },
                }}
              >
                <ListItemIcon>
                  <PdfIcon sx={{ color: '#d32f2f' }} />
                </ListItemIcon>
                               <ListItemText
                 primary={
                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                     PDF Report
                                   <Chip
                label="Suggested"
                size="small"
                sx={{
                  backgroundColor: '#1976d2',
                  color: '#ffffff',
                  fontSize: '0.7rem',
                  height: '20px',
                  '& .MuiChip-label': {
                    px: 0.5,
                  },
                }}
              />
                   </Box>
                 }
                 secondary="Download as PDF document"
                 primaryTypographyProps={{ fontWeight: 500 }}
                 secondaryTypographyProps={{ fontSize: '0.875rem' }}
               />
              </ListItemButton>
              <ListItemButton
                onClick={() => handleMilestoneReport('word')}
                sx={{
                  borderRadius: 1,
                  border: '1px solid #e0e0e0',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    borderColor: '#1976d2',
                  },
                }}
              >
                <ListItemIcon>
                  <WordIcon sx={{ color: '#1976d2' }} />
                </ListItemIcon>
                <ListItemText
                  primary="Word Report"
                  secondary="Download as Word document"
                  primaryTypographyProps={{ fontWeight: 500 }}
                  secondaryTypographyProps={{ fontSize: '0.875rem' }}
                />
              </ListItemButton>
            </List>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button
              onClick={() => setFormatDialog({ open: false, selectedMilestone: null })}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </div>
  );
};

export default MilestonesPage;
